import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  Paper,
  Divider,
  Button,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  School as LearnIcon,
  Psychology as ExplainIcon,
  Sports as ActivitiesIcon,
  Quiz as ExamIcon,
  Note as NotesIcon,
  Forum as HubChatIcon,
  Science as LabsIcon,
  Restore as RestoreIcon
} from '@mui/icons-material';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Feature Control Settings Component
 * Allows users to enable/disable specific tools for their account
 * Perfect for demos where you want to show only certain features
 * 
 * v11.12.29: Initial implementation
 */
function FeatureControlSettings({ user }) {
  const [features, setFeatures] = useState({
    learn: true,
    explain: true,
    activities: true,
    examPrep: true,
    notes: true,
    hubChat: true,
    labs: true
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load feature settings from Firestore
  useEffect(() => {
    const loadFeatureSettings = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists() && userDoc.data().featureFlags) {
          setFeatures(userDoc.data().featureFlags);
        }
      } catch (error) {
        console.error('Error loading feature settings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFeatureSettings();
  }, [user]);

  const handleToggle = (feature) => {
    setFeatures(prev => ({
      ...prev,
      [feature]: !prev[feature]
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    if (!user) {
      alert('Please sign in to save feature settings');
      return;
    }

    setSaving(true);
    console.log('💾 FeatureControlSettings: Saving feature flags for user', user.uid, features);
    try {
      await setDoc(
        doc(db, 'users', user.uid),
        {
          featureFlags: features,
          lastUpdated: new Date()
        },
        { merge: true }
      );
      
      console.log('✅ FeatureControlSettings: Feature flags saved successfully!');
      setSaved(true);
      
      // Show success message
      if (window.showToast) {
        window.showToast('✅ Feature settings saved successfully!', 'success');
      }
      
      // Hide success after 2 seconds
      setTimeout(() => setSaved(false), 2000);
      
      // Trigger a refresh to apply changes
      window.location.reload();
    } catch (error) {
      console.error('Error saving feature settings:', error);
      alert('Failed to save feature settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = async () => {
    const allEnabled = {
      learn: true,
      explain: true,
      activities: true,
      examPrep: true,
      notes: true,
      hubChat: true,
      labs: true
    };
    setFeatures(allEnabled);
    setSaving(true);
    try {
      await setDoc(
        doc(db, 'users', user.uid),
        { featureFlags: allEnabled, lastUpdated: new Date() },
        { merge: true }
      );
      setSaved(true);
      if (window.showToast) {
        window.showToast('✅ All features restored and saved!', 'success');
      }
      setTimeout(() => {
        setSaved(false);
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error restoring features:', error);
      alert('Failed to restore features. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const featureList = [
    {
      key: 'learn',
      label: 'Learn (Teacher Mode)',
      icon: <LearnIcon />,
      description: 'Comprehensive page explanations with examples, key points, and exam tips'
    },
    {
      key: 'explain',
      label: 'Explain (Smart Analysis)',
      icon: <ExplainIcon />,
      description: 'Detailed explanations with analogies, practice exercises, and solutions'
    },
    {
      key: 'activities',
      label: 'Activities (Interactive Learning)',
      icon: <ActivitiesIcon />,
      description: 'MCQs, practice questions, hands-on activities, and discussion prompts'
    },
    {
      key: 'examPrep',
      label: 'Exam Prep',
      icon: <ExamIcon />,
      description: 'Generate exam questions, MCQs, short answers, and long answer questions'
    },
    {
      key: 'notes',
      label: 'Notes',
      icon: <NotesIcon />,
      description: 'Rich text editor for taking and organizing notes'
    },
    {
      key: 'hubChat',
      label: 'Hub Chat',
      icon: <HubChatIcon />,
      description: 'AI-powered chat for questions and discussions about your PDF'
    },
    {
      key: 'labs',
      label: 'Labs (Advanced Tools)',
      icon: <LabsIcon />,
      description: 'Word-by-word analysis, read & understand, logic gate visualization, and more'
    }
  ];

  const enabledCount = Object.values(features).filter(Boolean).length;
  const totalCount = Object.keys(features).length;
  const hasDisabledFeatures = enabledCount < totalCount;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Alert severity="warning">
        <Typography variant="body2">
          <strong>Sign in required</strong>
          <br />
          Please sign in with Google to manage feature settings for your account.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Prominent warning banner when any feature is disabled */}
      {hasDisabledFeatures && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="warning"
              variant="contained"
              size="small"
              startIcon={saving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <RestoreIcon />}
              onClick={handleResetToDefault}
              disabled={saving}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {saving ? 'Restoring…' : 'Restore All'}
            </Button>
          }
        >
          <Typography variant="body2" fontWeight={600}>
            {totalCount - enabledCount} feature{totalCount - enabledCount > 1 ? 's are' : ' is'} currently disabled.
          </Typography>
          <Typography variant="body2">
            Some tools may not appear in the app. Click <strong>Restore All</strong> to enable everything.
          </Typography>
        </Alert>
      )}

      {/* Header with description */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>🎯 Feature Control Panel</strong>
          <br />
          Enable or disable specific tools in the application. This is perfect for demos, 
          presentations, or customizing the learning experience.
          <br />
          <strong>Note:</strong> Changes take effect after saving and will apply immediately.
        </Typography>
      </Alert>

      {/* Status Chip */}
      <Box sx={{ mb: 3, display: 'flex', gap: 1, alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Currently Active:
        </Typography>
        <Chip 
          label={`${enabledCount} of ${totalCount} features enabled`}
          color={enabledCount === totalCount ? 'success' : 'warning'}
          size="small"
        />
      </Box>

      {/* Feature Toggles */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        {featureList.map((feature, index) => (
          <Box key={feature.key}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 1.5 }}>
              <Box sx={{ mr: 2, mt: 0.5, color: features[feature.key] ? 'primary.main' : 'text.disabled' }}>
                {feature.icon}
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                  {feature.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={features[feature.key]}
                    onChange={() => handleToggle(feature.key)}
                    color="primary"
                  />
                }
                label=""
                sx={{ ml: 2 }}
              />
            </Box>
            {index < featureList.length - 1 && <Divider />}
          </Box>
        ))}
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<RestoreIcon />}
          onClick={handleResetToDefault}
          disabled={saving || !hasDisabledFeatures}
          size="medium"
        >
          Restore All Features
        </Button>
        
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          size="medium"
        >
          {saving ? 'Saving...' : saved ? '✓ Saved!' : 'Save Changes'}
        </Button>
      </Box>

      {/* Usage Tips */}
      <Alert severity="success" sx={{ mt: 3 }}>
        <Typography variant="body2" fontWeight={600} gutterBottom>
          💡 Demo Tips:
        </Typography>
        <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
          <li>Disable <strong>Labs</strong> to hide advanced technical features for beginner demos</li>
          <li>Enable only <strong>Learn + Explain</strong> for focused educational presentations</li>
          <li>Disable <strong>Exam Prep</strong> if focusing on interactive learning activities</li>
          <li>Keep <strong>Hub Chat</strong> enabled for Q&A during live demos</li>
        </Typography>
      </Alert>
    </Box>
  );
}

export default FeatureControlSettings;
